import {
  unwrapFieldValue,
  wrapFields,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { ComputedSectionSchema } from 'features/libra/sections/computedSection/computedSectionTypes'
import { FormLayoutItemSchema } from 'features/libra/sections/formLayout'
import { FormSectionSchema } from 'features/libra/sections/formSection/formSectionTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo } from 'react'
import { z } from 'zod'

const stringOrArraySchema = z
  .union([z.string(), z.array(z.string())])
  .transform((val) => {
    const arr = Array.isArray(val) ? val : [val]
    return [...new Set(arr)] as ReadonlyArray<string>
  })

export const damagePredictionConfidenceValues = ['high', 'med', 'low'] as const

export const confidenceValues = damagePredictionConfidenceValues

export const booleanOrUnknownOrNoHistoryValues = [
  'true',
  'false',
  'unknown',
  'no_history',
] as const

// Section schema for NII Screening - supports form and computed sections
const NiiScreeningSectionSchema = z.union([
  FormSectionSchema,
  ComputedSectionSchema,
])

export type NiiScreeningSection = z.infer<typeof NiiScreeningSectionSchema>

// New config-driven schema for NII Screening
export const niiScreeningConfigSchema = z.object({
  sections: z.array(NiiScreeningSectionSchema).optional(),
  form: z.array(FormLayoutItemSchema).optional(),
  // Legacy: reference fields for backwards compatibility
  referenceFields: z.record(z.string(), stringOrArraySchema).optional(),
})

export type NiiScreeningConfig = z.infer<typeof niiScreeningConfigSchema>

// Legacy schema for ticket props - kept for backwards compatibility
export const niiScreeningTicketPropsSchema = z
  .object({
    isIntrinsicallySuitable: z.enum(['true', 'false', 'unknown']).optional(),
    previousInspectionHistoryValidity: z
      .enum(booleanOrUnknownOrNoHistoryValues)
      .default('unknown'),
    areThereSimilarServiceAssets: z
      .enum(['true', 'false', 'unknown'])
      .optional(),
    confidenceInDamagePrediction: z
      .enum(damagePredictionConfidenceValues)
      .default('med'),
    suitabilityJustification: z.string().optional(),

    confidenceInPredictingDegradationTypeAndLocation: z
      .enum(confidenceValues)
      .default('med'),
    confidenceInPredictingDegradationTypeAndLocationJustification: z
      .string()
      .optional(),

    previousInspectionEffectiveness: z.enum(confidenceValues).default('med'),
    previousInspectionEffectivenessJustification: z.string().optional(),

    severityAndRateOfDegradation: z.enum(confidenceValues).default('med'),
    severityAndRateOfDegradationJustification: z.string().optional(),

    screeningOutcomeOverride: z.boolean().optional(),
    screeningOutcomeOverrideJustification: z.string().optional(),
  })
  .readonly()

export type NiiScreeningTicketProps = z.infer<
  typeof niiScreeningTicketPropsSchema
>

export type NiiScreeningField = keyof NiiScreeningTicketProps

// ============================================================================
// LEGACY CODE - KEPT FOR BACKWARDS COMPATIBILITY
// ============================================================================
// The following code is used by the legacy NII Screening card components
// (NiiSuitabilityCard, NiiSeverityAndRiskCard, NiiScreeningOutcome).
// When no sections are configured in the workflow JSON, the legacy mode is used.
//
// For new implementations, use the config-driven approach with:
// - sections array in config (form and computed sections)
// - ontologyFields in workflow.globalContext
// - useWorkflowFieldValues() hook for field management
// ============================================================================

/**
 * @deprecated Use config.sections with ontologyFields instead.
 * Keys used for NII screening data in fieldValues - legacy mode only.
 */
export const NII_SCREENING_FIELD_KEYS = [
  'isIntrinsicallySuitable',
  'previousInspectionHistoryValidity',
  'areThereSimilarServiceAssets',
  'confidenceInDamagePrediction',
  'suitabilityJustification',
  'confidenceInPredictingDegradationTypeAndLocation',
  'confidenceInPredictingDegradationTypeAndLocationJustification',
  'previousInspectionEffectiveness',
  'previousInspectionEffectivenessJustification',
  'severityAndRateOfDegradation',
  'severityAndRateOfDegradationJustification',
  'screeningOutcomeOverride',
  'screeningOutcomeOverrideJustification',
] as const satisfies ReadonlyArray<keyof NiiScreeningTicketProps>

/**
 * @deprecated Use useTicketAccess().setFieldValues() directly with config-driven approach.
 * Legacy hook for saving NII screening data - used when no sections are configured.
 */
export function useSaveTicketNiiScreeningData(): (
  niiScreening: NiiScreeningTicketProps
) => Promise<void> {
  const { setFieldValues } = useTicketAccess()
  return useCallback(
    async (niiScreening: NiiScreeningTicketProps) => {
      await setFieldValues(
        wrapFields(niiScreening as unknown as Record<string, unknown>)
      )
    },
    [setFieldValues]
  )
}

/**
 * @deprecated Use useTicketAccess().fieldValues directly with config-driven approach.
 * Legacy hook for loading NII screening data - used when no sections are configured.
 */
export function useTicketNiiScreeningData(): {
  isLoading: boolean
  niiScreening?: NiiScreeningTicketProps
} {
  const { fieldValues, isLoading } = useTicketAccess()
  const { errorNotification } = useGeckoNotifications()
  return useMemo(() => {
    // Extract and unwrap NII screening fields from flat fieldValues
    const niiScreeningValues: Record<string, unknown> = {}
    for (const key of NII_SCREENING_FIELD_KEYS) {
      if (key in fieldValues) {
        niiScreeningValues[key] = unwrapFieldValue(fieldValues[key])
      }
    }

    const niiScreening =
      niiScreeningTicketPropsSchema.safeParse(niiScreeningValues)
    if (!niiScreening.success) {
      errorNotification('Invalid NII screening data on ticket')
      return {
        isLoading,
        niiScreening: undefined,
      }
    }
    return {
      isLoading,
      niiScreening: niiScreening.data,
    }
  }, [fieldValues, isLoading, errorNotification])
}
